import React from 'react';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from 'reactstrap';
import styled from 'styled-components';
import { getTranslationKey } from '../../modules/utils';
import { CommentsMode } from '../../modules/types/comments';
import { useDisableCommentsModal } from './hooks/useDisableCommentsModal';

const Intro = styled.div`
  margin-bottom: 15px;
`;

const StyledLabel = styled(Label)`
  display: block;

  &:not(:last-child) {
    margin-bottom: 12px;
  }
`;

interface IProps {
  isOpen: boolean;
  closeCallback: () => void;
  confirmCallback: (mode: CommentsMode) => void;
}

/**
 * Shown when the photographer switches comments OFF in the collection settings
 * tab. Gallery presets deliberately do NOT use this - there is nothing to
 * migrate for a preset, so its toggle stays a plain switch.
 */
export const DisableCommentsModal: React.FC<IProps> = ({
  isOpen,
  closeCallback,
  confirmCallback
}) => {
  const { activeRadio, listData, selectMode, confirm, isConfirmDisabled } = useDisableCommentsModal(
    {
      isOpen,
      closeCallback,
      confirmCallback
    }
  );

  return (
    <Modal centered isOpen={isOpen} wrapClassName="modal-dark" size="lg" toggle={closeCallback}>
      <ModalHeader toggle={closeCallback}>
        {getTranslationKey('collectionSettings.disableComments.title')}
      </ModalHeader>
      <ModalBody data-cy="disableCommentsModalBody">
        <Intro>{getTranslationKey('collectionSettings.disableComments.text')}</Intro>
        <FormGroup check>
          {listData.map(({ name, label }) => (
            <StyledLabel className="w-100-p cursor-pointer" check key={name}>
              <Input
                type="radio"
                name="disableCommentsMode"
                data-cy={`disableCommentsOption-${name}`}
                checked={activeRadio === name}
                onChange={() => selectMode(name)}
              />
              <span className="inline">{label}</span>
            </StyledLabel>
          ))}
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button
          data-cy="disableCommentsCancelButton"
          size="md"
          onClick={closeCallback}
          className="btn btn-primary"
        >
          {getTranslationKey('collectionSettings.disableComments.cancelButton')}
        </Button>
        <Button
          data-cy="disableCommentsConfirmButton"
          size="md"
          color="success"
          disabled={isConfirmDisabled}
          onClick={confirm}
          className="btn btn-primary"
        >
          {getTranslationKey('collectionSettings.disableComments.confirmButton')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DisableCommentsModal;
